import type {
  OpportunityRelatedBundledExperience,
  OpportunityRelatedDeal,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import { parseValidDayjs } from '~/services/TimeService';

import type { ChecklistStatus } from '../../shared/checklistUtils';
import { getStatusLabel, normalizeString } from '../../shared/checklistUtils';

import type { BundledExperienceChecklistRow, DateComparison } from './types';

const BUNDLED_EXPERIENCES_FIELD = 'Bundled Experiences';

export function buildBundledExperienceRows({
  dealCurrency,
  hotelBookBy,
  hotelTravelTo,
  relatedBundledExperiences,
  relatedDeals,
}: {
  dealCurrency?: string;
  hotelBookBy?: string;
  hotelTravelTo?: string;
  relatedBundledExperiences?: Array<OpportunityRelatedBundledExperience>;
  relatedDeals?: Array<OpportunityRelatedDeal>;
}): Array<BundledExperienceChecklistRow> {
  const dealsById = new Map((relatedDeals ?? []).map((deal) => [String(deal.id), deal] as const));
  const activeBundledExperiences = (relatedBundledExperiences ?? []).filter(
    (bundledExperience) => !bundledExperience.inactive__c,
  );
  const groupedExperiences = new Map<string, Array<OpportunityRelatedBundledExperience>>();

  for (const bundledExperience of activeBundledExperiences) {
    const key = buildBundledExperienceKey(bundledExperience);
    groupedExperiences.set(key, [...(groupedExperiences.get(key) ?? []), bundledExperience]);
  }

  return Array.from(groupedExperiences.entries()).map(([key, experiences]) => {
    const sourceExperience = experiences[0];
    const parentDeal = dealsById.get(String(sourceExperience.deal_option__c));
    const bundledExperience = normalizeString(sourceExperience.name) ?? key;
    const linkedDealOptions = experiences
      .map((experience) => normalizeString(dealsById.get(String(experience.deal_option__c))?.name))
      .filter((name): name is string => !!name);
    const currency = normalizeString(sourceExperience.currencyisocode);
    const rowDealCurrency = dealCurrency ?? currency;
    const childAge = normalizeString(parentDeal?.child_age_permitted__c);
    const bundledBookBy = sourceExperience.bundled_experience_opportunity_book_by__c;
    const bundledTravelTo = sourceExperience.bundled_experience_opportunity_travel_to__c;
    const bookByComparison = compareBundledBookBy({ bookBy: bundledBookBy, hotelBookBy });
    const travelToComparison = compareBundledTravelTo({ hotelTravelTo, travelTo: bundledTravelTo });
    const validityOk = bookByComparison.ok && travelToComparison.ok;
    const blackoutsOk = false;
    const currencyOk = !!currency && !!rowDealCurrency && currency === rowDealCurrency;
    const childAgeOk = isBundledChildAgeOk(childAge);
    const status: ChecklistStatus =
      validityOk && blackoutsOk && currencyOk && childAgeOk ? 'approved' : 'requires approval';

    return {
      blackoutsOk,
      bookBy: bundledBookBy ?? undefined,
      bookByComparison,
      bundledExperience,
      childAge,
      childAgeOk,
      currency,
      currencyOk,
      dealCurrency: rowDealCurrency,
      field: BUNDLED_EXPERIENCES_FIELD,
      hotelBookBy,
      hotelTravelTo,
      id: `experience:${key}`,
      linkedDealOptions,
      status,
      statusLabel: getStatusLabel(status),
      travelTo: bundledTravelTo ?? undefined,
      travelToComparison,
      validityOk,
    };
  });
}

function buildBundledExperienceKey(bundledExperience: OpportunityRelatedBundledExperience) {
  return normalizeString(bundledExperience.ancillary_deal_option__c) ?? String(bundledExperience.id);
}

// Both validity checks are the same "earlier <= later" comparison, just on
// different date pairs (experience/hotel book-by, hotel/experience travel-to).
function compareChainedDates({ earlier, later }: { earlier?: string | null; later?: string | null }): DateComparison {
  const earlierDate = parseValidDayjs(earlier);
  const laterDate = parseValidDayjs(later);

  if (!earlierDate?.isValid() || !laterDate?.isValid()) {
    return { ok: false };
  }

  return { ok: !earlierDate.isAfter(laterDate, 'day') };
}

// No gap at the start of the hotel's validity window.
export function compareBundledBookBy({
  bookBy,
  hotelBookBy,
}: {
  bookBy?: string | null;
  hotelBookBy?: string;
}): DateComparison {
  return compareChainedDates({ earlier: hotelBookBy, later: bookBy });
}

// No gap at the end of the hotel's validity window.
export function compareBundledTravelTo({
  hotelTravelTo,
  travelTo,
}: {
  hotelTravelTo?: string;
  travelTo?: string | null;
}): DateComparison {
  return compareChainedDates({ earlier: hotelTravelTo, later: travelTo });
}

export function isBundledChildAgeOk(childAge?: string) {
  if (!childAge || !/\b(child|children|kid|kids)\b/i.test(childAge)) {
    return true;
  }

  return false;
}
